import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Grid,
} from '@mui/material';

const VendorReportCode: React.FC = () => {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accessCode || !/^\d{6}$/.test(accessCode)) {
      setError('Please enter a valid 6-digit access code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate code by trying to fetch the report
      const response = await fetch(`/api/agencies/vendor/report/code/${accessCode}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Invalid access code');
      }
      
      // Code is valid, navigate to report page
      navigate(`/vendor/report/code/${accessCode}`);
    } catch (err: any) {
      setError(err.message || 'Invalid access code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Market Appraisal Access
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Enter your 6-digit access code to view your market appraisal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your access code was sent to you via email. Please check your inbox for the code.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Access Code"
                  value={accessCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setAccessCode(value);
                  }}
                  placeholder="123456"
                  inputProps={{ 
                    maxLength: 6,
                    pattern: '[0-9]{6}',
                    style: { 
                      textAlign: 'center', 
                      fontSize: '1.8rem', 
                      letterSpacing: '0.8rem',
                      fontWeight: 'bold'
                    }
                  }}
                  error={!!error}
                  helperText={error || "Enter the 6-digit code from your email"}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& input': {
                        textAlign: 'center',
                        fontSize: '1.8rem',
                        letterSpacing: '0.8rem',
                        fontWeight: 'bold'
                      }
                    }
                  }}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !accessCode || accessCode.length !== 6}
                  sx={{ py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Access Report'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default VendorReportCode;

